import React, { useMemo, useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import JobRoutesDataTable from '../JobRoutesDataTable';
import AreaSelectBox from '../dropdowns/AreaSelectBox';
import TechniciansSelectBox from "../dropdowns/TechniciansSelectBox";
import FormTextInput from '../formHelpers/FormTextInput';
import JobTypeSelectBox from '../dropdowns/JobTypeSelectBox';
import FormDatePicker from '../formHelpers/FormDatePicker';
import FormSelectBox from '../formHelpers/FormSelectBox';
import { selectAuthUser } from '../../features/auth/authUserSlice';
import 'tailwindcss/tailwind.css';
import HeaderTitleBar from '../HeaderTitleBar';
import { fetchCompanies } from '../../features/companies/companiesSlice';
import SelectBox from '../fields/SelectBox';
import { titlize } from '../../utils/TextTools';
import moment from 'moment-timezone';
import {
  fetchJobRoutes,
  clearJobRoutes,
  clearData,
  setPageSize,
  setSelectedCompanyId,
  setSearchQuery,
  setSelectedAreaId,
  setSelectedUserId
} from '../../features/jobRoutes/jobRouteFilterSlice';

const Jobroutes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const authUser = useSelector((state) => state.authUser);
  const authUserRole = authUser.role;
  const [postcallType, setPostcallType] = useState('0');
  const [status, setStatus] = useState('0');
  const [userId, setUserId] = useState('0');
  const [selectedJobDate, setSelectedJobDate] = useState(new Date());
  const [jobDate, setJobDate] = useState(new Date());
  const [hhcCompleted, setHhcCompleted] = useState('');
  const [selectedJobType, setSelectedJobType] = useState('0');

  const StatusText = ({ field, row }) => {
    return titlize(row.original[field]);
  }

  const DateAdapter = ({ field, row }) => {
    if (row.original[field] === null) return;
    return row.original[field].toString();
  }

  const HhcAdapter = ({ field, row }) => {
    if (row.original[field] === null) {
      return 'pending';
    }

    if (row.original[field] === false) {
      return 'failed'
    }

    return 'passed';
  }

  const JobLink = ({ field, row }) => {
    return <Link
      to={`/billing/job_details/${row.original.id}?referral=${location.pathname}`}
      onClick={() => { navigate( `/billing/job_details/${row.original.id}` ) }}
      className="font-medium underline">
        {row.original[field]}
      </Link>;
  }

  const columns = useMemo(() => {
    if (localStorage.getItem('msoId').toString() === '4' || localStorage.getItem('msoId').toString() === '3') {
      return [
        { header: 'Tech ID', accessorKey: 'tech_id' },
        { header: 'Technician', accessorKey: 'full_name' },
        {
          header: 'Job Date',
          accessorKey: 'job_date',
          cell: ({ getValue, row}) => <DateAdapter field={'job_date'} value={getValue()} row={row} />
        },
        {
          header: 'Account Number',
          accessorKey: 'account_number',
          cell: ({ getValue, row }) => <JobLink field={'account_number'} value={getValue()} row={row} />
        },
        { header: 'Area', accessorKey: 'area_name' },
        { header: 'Job Number', accessorKey: 'job_number' },
        {
          header: 'Status',
          accessorKey: 'status',
          cell: ({ getValue, row }) => <StatusText field={'status'} value={getValue()} row={row} />
        },
        { header: 'Time', accessorKey: 'time_frame' },
        { header: 'Job Type', accessorKey: 'job_type' },
        {
          header: 'HHC',
          accessorKey: 'passed',
          cell: ({ getValue, row }) => <HhcAdapter field={'passed'} value={getValue()} row={row} />
        },
        { header: 'Postcall', accessorKey: 'postcall_complete' },
      ]
    } else {
      return [
        {
          header: 'Job Number',
          accessorKey: 'job_number',
          cell: ({ getValue, row }) => <JobLink field={'job_number'} value={getValue()} row={row} />
        },
        { header: 'Technician', accessorKey: 'user.full_name' },
        {
          header: 'Status',
          accessorKey: 'status',
          cell: ({ getValue, row }) => <StatusText field={'status'} value={getValue()} row={row} />
        },
        { header: 'Area', accessorKey: 'area_name' },
        { header: 'Tech ID', accessorKey: 'tech_id' },
        { header: 'Account Number', accessorKey: 'account_number' },
        { header: 'Job Type', accessorKey: 'job_type' },
        { header: 'Job Date', accessorKey: 'job_date' },
      ]
    }
  }, []);

  const dispatch = useDispatch();

  const {
    data,
    loading,
    pageCount,
    totalItems,
    pageSize,
    selectedCompanyId,
    selectedAreaId,
    selectedUserId,
    searchQuery
  } = useSelector((state) => state.jobRouteFilter);

  const { companyCollection } = useSelector((state) => state.companies);
  const [pageIndex, setPageIndex] = useState(0);
  const [sorting, setSorting] = useState([{ id: 'job_date', desc: true, selected: true }]);
  const [sortOverride, setSortOverride] = useState(true);

  const canSelectCompany = () => {

    return /admin|mso_user/.test(authUserRole);
  }

  const fetchCompaniesData = useCallback(() => {
    dispatch(fetchCompanies({ page: 1, per: 9999, navigate }));
  }, [ dispatch, navigate]);

  useEffect(() => {
    if (!canSelectCompany()) return;

    fetchCompaniesData();
  }, [ fetchCompaniesData, dispatch ])

  const handleSelectedCompanyId = (value) => {
    /* TODO - REMOVE THIS LATER
    */
    localStorage.setItem('companyId', value);
    dispatch(setSelectedCompanyId(value));
  };

  const {
    techCollection,
    loading: techsLoading
  } = useSelector((state) => state.technicians);

  const fetchJobRoutesData = useCallback(() => {
    dispatch(fetchJobRoutes({
      companyId: selectedCompanyId ,
      areaId: selectedAreaId,
      userId: selectedUserId,
      hhcCompleted: hhcCompleted,
      jobDate: jobDate,
      jobType: selectedJobType,
      status: status,
      page: pageIndex + 1,
      per: pageSize,
      search: searchQuery,
      postcall: postcallType,
      sorting
    }));
  }, [dispatch,
        location.pathname,
        status,
        jobDate,
        selectedJobType,
        hhcCompleted,
        selectedCompanyId,
        selectedAreaId,
        selectedUserId,
        pageIndex,
        pageSize,
        searchQuery,
        postcallType,
        sorting
      ]);

  useEffect(() => {
    dispatch(clearJobRoutes())
  }, [location.pathname])

  useEffect(() => {
    let otherCompany = selectedCompanyId;

    if (!canSelectCompany()) {
      dispatch(setSelectedCompanyId(authUser.company_id));
      otherCompany = authUser.company_id
    }

    const canViewWithoutCompany = (
      authUser.role == 'administrator' || authUser.role == 'mso_user' || (
        authUser.company_id === 0 && /corporate|supervisor/.test(authUser.user_type)
      )
    )
    if (canViewWithoutCompany) {
      fetchJobRoutesData()
      return;
    }

    if (otherCompany === null) return;

    if (otherCompany >= 0) {
      fetchJobRoutesData();
    } else {
      dispatch(clearJobRoutes());
    }
  }, [selectedCompanyId, selectedAreaId, fetchJobRoutesData, dispatch]);

  const handlePageChange = (newPageIndex, newPageSize) => {
    setPageIndex(newPageIndex);
    if (newPageSize !== pageSize) dispatch(setPageSize(newPageSize));
  };

  const handleSortingChange = (newSorting) => {
    setSorting(newSorting);
    setSortOverride(!sortOverride);
  };

  const handleSearchChange = (newSearchQuery) => {
    dispatch(setSearchQuery(newSearchQuery));
    setPageIndex(0);
  };

  const handlePostCallChange = (e) => {
    const { name, value } = e.target;
    setPostcallType(value);
  };

  const handleJobDateChange = (date) => {
    if(date === null) return ""
    setJobDate(date)
  };

  const handleAreaChange = (e) => {
    const { value } = e.target;
    dispatch(setSelectedAreaId(value));
  }

  const handleHhcCompleted = (e) => {
    const { value } = e.target;
    setHhcCompleted(value)
  }

  const handleJobTypeChange = (e) => {
    const { value } = e.target;
    setSelectedJobType(value);
  }

  const handleTechChange = (e) => {
    const { name, value } = e.target;
    dispatch(setSelectedUserId(value));
  };

  const ToolBox = () => {

    return (
      <div className="grid grid-cols-4 gap-2 mt-2">
        <FormSelectBox
          name='postcall_complete'
          data={[
            { id: 1, name: 'Completed' },
            { id: 2, name: 'Incomplete' },
          ]}
          defaultValue={postcallType}
          onChange={(e) => handlePostCallChange(e) }
          valueField={'id'}
          keyField={'name'}
          zeroOption={'Select Postcalls (All)'}
        />
        {/* <TechniciansSelectBox
          value={selectedUserId || "0"}
          zeroOption={'Select a Technician (All)'}
          onChange={handleTechChange}
          data={techCollection}
          areaId={selectedAreaId}
          companyId={selectedCompanyId}
          useTarget={false}
        /> */}
       <FormSelectBox
          name='status'
          data={[
            { id: 1, name: "Completed" },
            { id: 2, name: "Pending" }
           ]
          }
          defaultValue={status}
          onChange={(e) => setStatus(e.target.value) }
          valueField={'id'}
          keyField={'name'}
          zeroOption={'Select Job Status (All)'}
        />
        <AreaSelectBox
          name={'area_id'}
          value={selectedAreaId}
          onChange={handleAreaChange}
          useTarget={false}
          zeroOption={'Select an Area (All)'}
        />
        <FormDatePicker
          name="job_date"
          onChange={handleJobDateChange}
          placeholder="Enter a Job Date"
          value={jobDate}
        />
        <FormSelectBox
          name='hhc_completed'
          data={[
            { id: 1, name: 'Completed' },
            { id: 2, name: 'Incomplete' },
          ]}
          defaultValue={hhcCompleted}
          onChange={handleHhcCompleted}
          valueField={'id'}
          keyField={'name'}
          zeroOption={'Select HealthCheck Completed (All)'}
        />
        <JobTypeSelectBox
          name={'jobType'}
          value={selectedJobType}
          onChange={handleJobTypeChange}
          useTarget={false}
          zeroOption={'Select an Job Type (All)'}
        />
      </div>
    )
  };

  return (
    <>
      <div className="p-4 mb-6 bg-white shadow rounded-lg">
        <HeaderTitleBar
          title='Job Routes'
          showBtn={false}
        />
        {canSelectCompany() && (
          <div className="mt-4">
            <SelectBox
              data={companyCollection}
              defaultValue={selectedCompanyId}
              onChange={handleSelectedCompanyId}
              valueField={'id'}
              keyField={'name'}
              zeroOption={'Select a Company (All)'}
            />
        </div>
        )}
      </div>

      <JobRoutesDataTable
        columns={columns}
        data={data}
        pageCount={pageCount}
        totalItems={totalItems}
        pageIndex={pageIndex}
        pageSize={pageSize}
        sorting={sorting}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={(size) => handlePageChange(pageIndex, size)}
        onSortingChange={handleSortingChange}
        onSearchChange={handleSearchChange}
        toolbox={<ToolBox />}
      />
    </>
  );
};

export default Jobroutes;